import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { NotificationsService } from './Service';
import { useAuthContext } from '../contexts/AuthContext';
import { PushNotification } from '../PushNotification';
import { WebSocketManager } from '../WebSocketConfig';
import { baseURL } from '../AxiosConfig';
import { PushActivationPrompt } from './PushActivationPrompt';
import { useAppContext } from '../contexts/AppContext';
import { Bell, X } from 'lucide-react';

const service = new NotificationsService();

const FETCH_LIMIT = 30;
const LIST_POLL_INTERVAL_MS = 5 * 60 * 1000;
const COUNT_POLL_INTERVAL_MS = 60 * 1000;
const PUSH_PROMPT_STORAGE_KEY_PREFIX = 'push_notification:daily_prompt:last_date';

interface Notification {
    _id: string;
    title: string;
    message: string;
    imageId?: string;
    path?: string;
    createdAt: Date | string;
    type?: string;
    visualizedAt?: Date | string;
    createdBy?: string;
}

interface NotificationsContextProps {
    notifications: Notification[];
    dropdownOpen: boolean;
    setDropdownOpen: (val: boolean) => void;
    onlyNotVisualized: boolean;
    setOnlyNotVisualized: (val: boolean) => void;
    countNotVisualized: number;
    loadNotifications: () => Promise<void>;
    loadMoreNotifications: () => Promise<void>;
    hasMoreNotifications: boolean;
    loadingNotifications: boolean;
    showNotification: (notification: Notification) => void;
}

const getLocalDateKey = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getDailyPushPromptStorageKey = (userId?: string | null) =>
    `${PUSH_PROMPT_STORAGE_KEY_PREFIX}:${userId || 'anonymous'}`;

const getStoredValue = (key: string) => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const setStoredValue = (key: string, value: string) => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Ignora erro de storage para nao quebrar a inicializacao do app.
    }
};

const NotificationsContext = createContext<NotificationsContextProps | undefined>(undefined);

export const useNotificationsContext = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotificationsContext must be used within a NotificationsProvider');
    }
    return context;
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuthContext();
    const { successSnackbar } = useAppContext();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [onlyNotVisualized, setOnlyNotVisualized] = useState(localStorage.getItem('onlyNotVisualized') === 'true');
    const [data, setData] = useState<Notification[]>([]);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [countNotVisualized, setCountNotVisualized] = useState<number>(0);
    const [visibleNotifications, setVisibleNotifications] = useState<
        { notification: Notification; animatingOut: boolean }[]
    >([]);
    const [page, setPage] = useState(1);
    const [hasMoreNotifications, setHasMoreNotifications] = useState(false);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [showPushActivationPrompt, setShowPushActivationPrompt] = useState(false);
    const [pushPromptLoading, setPushPromptLoading] = useState(false);
    const [pushPromptPermission, setPushPromptPermission] = useState<NotificationPermission | null>(null);

    const loadUnreadCount = useCallback(async () => {
        try {
            const response = await service.getCount();
            const count = Number(response?.data?.unreadCount || 0);
            setCountNotVisualized(count);
        } catch (error) {
            console.error('Error fetching notifications count:', error);
        }
    }, []);

    const loadNotificationsPage = useCallback(async (targetPage: number, append: boolean) => {
        try {
            setLoadingNotifications(true);
            const response = await service.getAll({ page: targetPage, limit: FETCH_LIMIT });
            const payload = response?.data;
            const items: Notification[] = Array.isArray(payload?.items)
                ? (payload.items as Notification[])
                : Array.isArray(payload)
                    ? (payload as Notification[])
                    : [];

            setData((prev) => {
                if (!append) {
                    return items;
                }

                const byId = new Map<string, Notification>();
                prev.forEach((item) => byId.set(item._id, item));
                items.forEach((item) => byId.set(item._id, item));
                return Array.from(byId.values());
            });

            setPage(targetPage);
            setHasMoreNotifications(Boolean(payload?.hasMore));

            const unreadCount = payload?.unreadCount;
            if (typeof unreadCount === 'number') {
                setCountNotVisualized(unreadCount);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoadingNotifications(false);
        }
    }, []);

    const loadNotifications = useCallback(async () => {
        await loadNotificationsPage(1, false);
    }, [loadNotificationsPage]);

    const loadMoreNotifications = useCallback(async () => {
        if (loadingNotifications || !hasMoreNotifications) return;
        await loadNotificationsPage(page + 1, true);
    }, [hasMoreNotifications, loadNotificationsPage, loadingNotifications, page]);

    const showNotification = useCallback((notification: Notification) => {
        const { _id, title, message, imageId } = notification;
        const newNotification: Notification = {
            _id,
            title,
            message,
            imageId,
            createdAt: new Date(),
        };

        setVisibleNotifications((prev) => [...prev, { notification: newNotification, animatingOut: false }]);

        const notificationSound = new Audio('notification.mp3');
        notificationSound.play().catch((error) => console.error('Erro ao reproduzir som:', error));

        setTimeout(() => {
            setVisibleNotifications((prev) =>
                prev.map((n) => (n.notification._id === newNotification._id ? { ...n, animatingOut: true } : n))
            );

            setTimeout(() => {
                setVisibleNotifications((prev) => prev.filter((n) => n.notification._id !== newNotification._id));
            }, 1000);
        }, 5000);
    }, []);

    const closePushActivationPrompt = useCallback(() => {
        setShowPushActivationPrompt(false);
    }, []);

    const activatePushFromPrompt = useCallback(async () => {
        if (!user?.uid) return;

        setPushPromptLoading(true);
        try {
            const permission = await PushNotification.requestAndSaveToken({ userId: user.uid });
            const nextPermission =
                permission ||
                (typeof Notification !== 'undefined' ? Notification.permission : null);

            if (nextPermission === 'granted') {
                successSnackbar('Notificações ativadas.');
                setShowPushActivationPrompt(false);
                return;
            }

            if (nextPermission === 'denied') {
                setPushPromptPermission('denied');
                return;
            }

            setShowPushActivationPrompt(false);
        } finally {
            setPushPromptLoading(false);
        }
    }, [successSnackbar, user?.uid]);

    useEffect(() => {
        if (!user?.uid) return;

        void loadNotifications();
        void loadUnreadCount();

        const ensurePushFlowOnAppOpen = async () => {
            if (typeof window === 'undefined' || typeof Notification === 'undefined') {
                return;
            }

            const currentPermission = Notification.permission;

            if (currentPermission === 'granted') {
                await PushNotification.requestAndSaveToken({ userId: user.uid });
                return;
            }

            const storageKey = getDailyPushPromptStorageKey(user.uid);
            const today = getLocalDateKey();
            const lastPromptDate = getStoredValue(storageKey);
            if (lastPromptDate === today) {
                return;
            }

            setStoredValue(storageKey, today);
            setPushPromptPermission(currentPermission);
            setShowPushActivationPrompt(true);
        };

        void ensurePushFlowOnAppOpen();

        const listInterval = setInterval(() => {
            void loadNotifications();
        }, LIST_POLL_INTERVAL_MS);

        const countInterval = setInterval(() => {
            void loadUnreadCount();
        }, COUNT_POLL_INTERVAL_MS);

        const webSocketManager = new WebSocketManager();
        webSocketManager.onReady(() => {
            webSocketManager.onChannel('newNotification', (notification?: Notification) => {
                if (!notification?._id) {
                    void loadNotifications();
                    return;
                }

                showNotification(notification);

                setData((prev) => [notification, ...prev.filter((item) => item._id !== notification._id)]);

                void loadUnreadCount();
            });
        });

        return () => {
            clearInterval(listInterval);
            clearInterval(countInterval);
            webSocketManager.disconnect();
        };
    }, [loadNotifications, loadUnreadCount, showNotification, user?.uid]);

    useEffect(() => {
        if (user?.uid) return;
        setData([]);
        setNotifications([]);
        setCountNotVisualized(0);
        setPage(1);
        setHasMoreNotifications(false);
        setLoadingNotifications(false);
        setShowPushActivationPrompt(false);
        setPushPromptLoading(false);
        setPushPromptPermission(null);
    }, [user?.uid]);

    useEffect(() => {
        setNotifications(onlyNotVisualized ? data.filter((e) => !e.visualizedAt) : data);
    }, [data, onlyNotVisualized]);

    useEffect(() => {
        if (countNotVisualized > 0) {
            document.title = `(${countNotVisualized}) ${document.title.replace(/^\(\d+\)\s*/, '')}`;
            return;
        }

        document.title = document.title.replace(/^\(\d+\)\s*/, '');
    }, [countNotVisualized]);

    useEffect(() => {
        if (!dropdownOpen) return;
        void loadNotifications();
    }, [dropdownOpen, loadNotifications]);

    return (
        <NotificationsContext.Provider
            value={{
                notifications,
                countNotVisualized,
                dropdownOpen,
                loadNotifications,
                loadMoreNotifications,
                hasMoreNotifications,
                loadingNotifications,
                setDropdownOpen,
                onlyNotVisualized,
                setOnlyNotVisualized: (value) => {
                    setOnlyNotVisualized(value);
                    localStorage.setItem('onlyNotVisualized', value.toString());
                },
                showNotification,
            }}
        >
            <div
                className="fixed"
                style={{
                    zIndex: 9999999,
                    width: '320px',
                    top: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                }}
            >
                {visibleNotifications.map(({ notification, animatingOut }) => (
                    <div
                        key={notification._id}
                        role="alert"
                        style={{ width: '320px' }}
                        className={`alert shadow-xl animate__animated ${animatingOut ? 'animate__backOutUp' : 'animate__backInDown'} mb-3`}
                    >
                        <div className="flex justify-between" style={{ width: '290px' }}>
                            <div className="flex gap-2 items-center">
                                <Bell className="text-primary" aria-hidden="true" />

                                <div className="justify-items-start">
                                    <h3 className="font-bold">{notification.title}</h3>
                                    <div className="text-xs">{notification.message}</div>
                                    {!!notification.imageId && (
                                        <img
                                            src={`${baseURL}/files/${notification.imageId}`}
                                            alt="Imagem da notificação"
                                            className="mt-2 max-h-24 w-auto rounded-md border border-base-300"
                                            loading="lazy"
                                        />
                                    )}
                                </div>
                            </div>

                            <button
                                className="sf-close-button btn btn-sm btn-circle"
                                onClick={() => {
                                    setVisibleNotifications((prev) =>
                                        prev.map((n) =>
                                            n.notification._id === notification._id ? { ...n, animatingOut: true } : n,
                                        ),
                                    );

                                    setTimeout(() => {
                                        setVisibleNotifications((prev) =>
                                            prev.filter((n) => n.notification._id !== notification._id),
                                        );
                                    }, 1000);
                                }}
                            >
                                <X aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {showPushActivationPrompt && (
                <PushActivationPrompt
                    permission={pushPromptPermission}
                    loading={pushPromptLoading}
                    onActivate={() => void activatePushFromPrompt()}
                    onClose={closePushActivationPrompt}
                />
            )}

            {children}
        </NotificationsContext.Provider>
    );
};
